import type { DataRouter } from "react-router";

type RecoveryRouter = Pick<DataRouter, "state" | "subscribe">;

const LAST_RELOAD_KEY = "chunk-load-recovery-reloaded-at";
const RELOAD_GUARD_MS = 60_000;

/**
 * A tab that outlives a deploy can request chunks its release built but the bucket no longer holds.
 * Reload once so the tab picks up the current release. A failed navigation still commits its URL
 * (React Router renders the route error), so wait for it to settle and reload the page the user
 * asked for, not the one they left.
 */
export function registerChunkLoadRecovery(router: RecoveryRouter): () => void {
  const controller = new AbortController();

  window.addEventListener("vite:preloadError", () => recover(router), { signal: controller.signal });

  return () => controller.abort();
}

function recover(router: RecoveryRouter): void {
  if (!claimReload()) {
    return;
  }

  if (router.state.navigation.state === "idle") {
    window.location.reload();

    return;
  }

  const unsubscribe = router.subscribe((state) => {
    if (state.navigation.state !== "idle") {
      return;
    }

    unsubscribe();
    window.location.reload();
  });
}

// Allow one reload per guard window, so a chunk that is missing from the current release shows the
// error page instead of reloading forever. Without session storage, never reload.
function claimReload(): boolean {
  try {
    const lastReload = Number(sessionStorage.getItem(LAST_RELOAD_KEY));
    if (lastReload && Date.now() - lastReload < RELOAD_GUARD_MS) {
      return false;
    }

    sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()));

    return true;
  } catch {
    return false;
  }
}
